"use client";

import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import Heading from "@/components/molecules/Heading";
import BupiLink from "@/components/atoms/link";
import { InfoTextWithImageBlockData } from "@/page-builder/fragments/InfoTextWithImageBlock/types";
import BupiLinkButton from "@/components/atoms/linkButton";
import WrapperBlueStyle from "@/components/atoms/wrapper-blue-style";
import { convertLexicalWithInternalLinks } from "@/lib/lexical";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { getAccessibleImageProps } from "@/lib/image-alt";

const shapeThemeColors = {
  terrain1: "#FEF8F6",
  terrain2: "#FDEEE8",
  terrain3: "#FADACC",
  terrain4: "#F6B89B",
  terrain5: "#E16F60",
} as any;

const ModuloTexto = ({
  title,
  description,
  image,
  imagePosition,
  ctas,
  hasShapeAnimation,
  shapeTheme,
  background
}: InfoTextWithImageBlockData) => {

  const shapeColor = shapeThemeColors[shapeTheme] || shapeThemeColors.terrain_2;
  const {isMobile} = useMediaQuery();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  const imageDirection = imagePosition === "left" ? -1 : 1;
  const svgDirection = imagePosition === "left" ? 1 : -1;
  
  const floatAnimation = hasShapeAnimation ? {
    y: [0, -10, 0],
    transition: {
      y: {
        repeat: Infinity,
        duration: 3,
        ease: "easeInOut"
      }
    }
  } : {};

  const Content = () => (
    <section ref={sectionRef} className="container mx-auto">
      <div
        className={`grid grid-cols-1 md:grid-cols-8 ${hasShapeAnimation ? "gap-32" : "gap-32"} md:items-center`}
      >
        <div
          className={`relative flex col-span-4 justify-end ${
            imagePosition === "left" ? "md:order-1" : "md:order-2"
          } ${isMobile ? "order-2" : " order-1"}
          }  ${hasShapeAnimation ? "xl:pl-[132px]" : "xl:px-32"}`}
        >
          {hasShapeAnimation && (
            <div className="absolute top-48 -right-24 z-10 md:-top-16 md:-right-8 xl:absolute xl:top-40 xl:left-112">
              <motion.svg 
                width="229" 
                height="46" 
                viewBox="0 0 229 46" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                className="w-[124px] h-[25px] xl:w-[229px] xl:h-[46px]"
                initial={{ opacity: 0, x: svgDirection * 80 }}
                animate={!isInView ? { 
                  opacity: 1, 
                  x: 0,
                  ...floatAnimation
                } : {} as any}
                transition={{ 
                  x: { duration: 2, ease: "easeOut" },
                  opacity: { duration: 1.5 },
                  delay: 0.5
                }}
              >
                <path d="M229 0H0L89.9373 46L229 0Z" fill={shapeColor} />
              </motion.svg>
            </div>
          )}
          
          <motion.img
            src={image?.url || ""}
            {...getAccessibleImageProps(image?.alt)}
            width={592}
            height={592}
            className="w-full h-auto"
             initial={!isMobile ? { opacity: 100, x: imageDirection * 80 } : {}}
              animate={!isMobile ? { opacity: 1, x: 0 } : {}}
              transition={!isMobile ? { 
                x: { duration: 2, ease: "easeOut" },
                opacity: { duration: 1.5 }
              } : {}}
          />
        </div>
        <div
          className={`flex col-span-4 flex-col justify-center space-y-8  ${
            imagePosition === "left" ? "md:order-2" : "md:order-1"
          } ${isMobile ? "order-2" : "order-1"}`}
        >
          <Heading title={title} />

          <div
            
            className="text-m-regular text-neutral-900 prose prose-ul:my-0 marker:text-neutral-900 py-8"
            dangerouslySetInnerHTML={{
              __html: convertLexicalWithInternalLinks( description as any ) ,
            }}
          />
          <div className="flex flex-col md:flex-row md:items-start mt-12 gap-8 md:gap-0 md:space-x-24">
            {ctas &&
              ctas.map((cta: any, index: number) =>
                cta?.style === "solid" ? (
                  <BupiLinkButton
                    key={index}
                    label={cta.label}
                    link={cta.url}
                    target={cta.target}
                    hasIcon
                    leadingIcon="agora-line-arrow-right-circle"
                  />
                ) : (
                  <BupiLink
                    key={index}
                    label={cta.label}
                    url={cta.url}
                    target={cta.target}
                    hasIcon
                    className="py-16"
                  />
                )
              )}
          </div>
        </div>
      </div>
    </section>
  );
  
  return background === "green" ? (
    <WrapperBlueStyle>
      <Content />
    </WrapperBlueStyle>
  ) : (
    <Content />
  );
};

export default ModuloTexto;
